import '@chessmate/ui/theme.css';
import '@chessmate/ui/tokens.css';
import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { App } from './App.js';
import { ThemeProvider } from '@chessmate/ui';
import { edition } from './edition/index.js';
import { I18nProvider } from './i18n/I18nProvider.js';
import { registerWebOnlyGames } from './lib/web-only-games.js';
import { primeGameStagesFromCache, startGameStagesSync } from './services/game-stages-sync.js';
import { initAudio } from './services/audio.js';
import { applyDynamicType } from './lib/dynamic-type.js';

// The screen audit boots the dev server with AUDIT_CAPTURE=1. Its
// goldens are pixel comparisons, and `.lift` transitions a card's transform
// over 150ms on hover — Playwright's mouse stays where it last clicked, so a
// step that clicks one card and screenshots another catches the first mid
// transition and the capture differs by a pixel run to run. See the
// `.reduce-motion` block in packages/ui/src/theme.css.
if (import.meta.env.AUDIT_CAPTURE === '1') {
  document.documentElement.classList.add('reduce-motion');
}

// Native DEBUG bundle only (`pnpm build:native:debug`): skip onboarding with the fake engine and show the diagnostics panel. In every
// other build the test below is the literal `false` (it must stay inline, see vite-env.d.ts), so this branch and the panel module are removed.
const NativeDebugPanel = typeof __NATIVE_DEBUG__ !== 'undefined' && __NATIVE_DEBUG__ ? lazy(() => import('./dev/native-debug.js')) : null;
if (typeof __NATIVE_DEBUG__ !== 'undefined' && __NATIVE_DEBUG__) window.history.replaceState(null, '', '/?onboarding=done&engine=fake');

// The kids edition reads 18px small text on a phone (`html.edition-kids` in packages/ui/src/theme.css); the adult edition keeps 16px.
if (edition.id === 'kids') document.documentElement.classList.add('edition-kids');

applyDynamicType(window);

const root = document.getElementById('root');
if (root) {
  // The operator's per-game stages (docs/proposals/remote-game-stages.md): the cached copy is applied BEFORE the first render so a game the
  // operator hid does not flash on screen; the network refresh starts after it. Every failure falls back to the build's own stages.
  primeGameStagesFromCache();
  initAudio();
  // Web-only games this build includes (none in a default or native build) register before the first render, so the Games page sees them.
  void registerWebOnlyGames().then(() => {
    startGameStagesSync();
    ReactDOM.createRoot(root).render(
      <React.StrictMode>
        <I18nProvider>
          <ThemeProvider defaultAccent={edition.accent ?? 'default'}>
            <App />
            {NativeDebugPanel && (
              <Suspense fallback={null}>
                <NativeDebugPanel />
              </Suspense>
            )}
          </ThemeProvider>
        </I18nProvider>
      </React.StrictMode>,
    );
  });
}
